import type { EventPart, EventPartOption, VideoArea } from './types';

export const UNIFIED_RECORDINGS_MODEL_EVENT_CREATED_CUTOFF = 1667260800000; // 1 Nov 2022
export const UNIFIED_RECORDINGS_MODEL_EVENT_START_CUTOFF = 1687816799999; // 26 Jun 2023

export const REPLAY_EVENT_PART: EventPart = {
  eventPartId: null,
  eventPartType: null,
  eventPartName: null,
};

export const createReplayEventPartOption = (
  label: string,
): EventPartOption => ({
  kind: 'replay',
  ...REPLAY_EVENT_PART,
  label,
});

export const createEventPartOption = (
  eventPart: EventPart,
  replayLabel: string,
): EventPartOption => {
  if (eventPart.eventPartType === null) {
    return createReplayEventPartOption(replayLabel);
  }

  const videoArea = {
    id: eventPart.eventPartId,
    name: eventPart.eventPartName,
    type: eventPart.eventPartType,
  };

  return {
    kind: 'video-area',
    videoArea,
    ...eventPart,
  };
};

export const createEventPartOptions = (
  videoAreas: VideoArea[],
  replayLabel: string,
): EventPartOption[] => {
  const videoAreaOptions = videoAreas.map(videoArea => ({
    kind: 'video-area' as const,
    videoArea,
    eventPartId: videoArea.id,
    eventPartType: videoArea.type,
    eventPartName: videoArea.name,
  }));

  return videoAreas.length > 1
    ? [createReplayEventPartOption(replayLabel), ...videoAreaOptions]
    : videoAreaOptions;
};

export const isSameEventPart = (left: EventPart, right: EventPart): boolean =>
  left.eventPartId === right.eventPartId &&
  left.eventPartType === right.eventPartType;

export const getEventPartOptionLabel = (option: EventPartOption): string =>
  option.kind === 'replay' ? option.label : option.videoArea.name;

export const getEventPartOption = (
  options: EventPartOption[],
  eventPart: EventPart | null | undefined,
) => {
  if (!eventPart) return null;

  const option = options.find(op => isSameEventPart(op, eventPart));

  return option ?? null;
};

export const optionToEventPart = (option: EventPartOption): EventPart =>
  option.kind === 'video-area'
    ? {
        eventPartId: option.eventPartId as VideoArea['id'],
        eventPartType: option.eventPartType as VideoArea['type'],
        eventPartName: option.videoArea.name,
      }
    : REPLAY_EVENT_PART;
